import { useState, useEffect, useRef } from "react";
import { ArrowDownToLine, ArrowUpFromLine } from "lucide-react";
import ImportModal from "./ImportModal";
import { downloadExport } from "@/lib/io";

/**
 * Standardised Import + Export button pair used on Companies, Inventory, Contacts.
 *
 * Props:
 *   entity          string                 "companies" | "inventory" | "contacts"
 *   rows            object[]               currently filtered rows (used by Export)
 *   exportColumns   string[] (optional)    column ordering for the export file
 *   exportName      string                 base filename (e.g. "edenstone-companies")
 *   onImported      () => void             callback after a successful import
 */
export default function ImportExportButtons({ entity, rows, exportColumns, exportName, onImported }) {
  const [showImport, setShowImport] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const menuRef = useRef(null);

  // Esc closes the export dropdown (for parity with Modal).
  useEffect(() => {
    if (!showExportMenu) return;
    const onKey = (e) => { if (e.key === "Escape") setShowExportMenu(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showExportMenu]);

  const doExport = (format) => {
    setShowExportMenu(false);
    downloadExport({
      filename: exportName,
      rows: rows || [],
      columns: exportColumns,
      format,
    });
  };

  return (
    <>
      <button
        data-testid={`${entity}-import-button`}
        onClick={() => setShowImport(true)}
        className="inline-flex items-center justify-center gap-2 h-10 px-3 sm:px-4 bg-brand-600 text-white text-sm font-semibold rounded-lg hover:bg-brand-700 transition-colors"
      >
        <ArrowDownToLine className="w-4 h-4" /> <span className="hidden sm:inline">Import</span>
      </button>

      <div className="relative" ref={menuRef}>
        <button
          data-testid={`${entity}-export-button`}
          onClick={() => setShowExportMenu((v) => !v)}
          className="inline-flex items-center justify-center gap-2 h-10 px-3 sm:px-4 bg-white text-zinc-700 text-sm font-semibold rounded-lg border border-zinc-300 hover:border-brand-500 hover:text-brand-700 transition-colors"
        >
          <ArrowUpFromLine className="w-4 h-4" /> <span className="hidden sm:inline">Export</span>
        </button>
        {showExportMenu && (
          <>
            <div className="fixed inset-0 z-30" onClick={() => setShowExportMenu(false)} />
            <div className="absolute right-0 mt-2 w-44 bg-white border border-zinc-200 rounded-lg shadow-lg z-40 overflow-hidden">
              <button
                data-testid={`${entity}-export-csv`}
                onClick={() => doExport("csv")}
                className="block w-full text-left px-4 py-2.5 text-sm hover:bg-zinc-50 border-b border-zinc-100"
              >
                Download as CSV
              </button>
              <button
                data-testid={`${entity}-export-xlsx`}
                onClick={() => doExport("xlsx")}
                className="block w-full text-left px-4 py-2.5 text-sm hover:bg-zinc-50"
              >
                Download as Excel (.xlsx)
              </button>
            </div>
          </>
        )}
      </div>

      {showImport && (
        <ImportModal
          entity={entity}
          onClose={() => setShowImport(false)}
          onImported={() => { setShowImport(false); onImported?.(); }}
        />
      )}
    </>
  );
}
